import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/structuredLogging';

export interface ServiceError extends Error {
  code?: string;
  details?: any;
  statusCode?: number;
}

export interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
};

export abstract class BaseService {
  protected readonly serviceName: string;
  protected readonly retryConfig: RetryConfig;

  constructor(serviceName: string, retryConfig?: Partial<RetryConfig>) {
    this.serviceName = serviceName;
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  }

  /**
   * Execute a function with error handling and retry logic
   */
  protected async execute<T>(
    operation: () => Promise<T>,
    context: Record<string, any> = {}
  ): Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = await this.executeWithRetry(operation);
      
      const duration = performance.now() - startTime;
      logger.info(`${this.serviceName} operation completed`, {
        component: this.serviceName,
        action: 'execute',
        metadata: { duration, ...context },
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      logger.error(`${this.serviceName} operation failed`, {
        component: this.serviceName,
        action: 'execute',
        metadata: { duration, ...context },
      }, error as Error);
      
      throw this.formatError(error);
    }
  }

  /**
   * Execute with exponential backoff retry
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    attempt: number = 0
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (!this.shouldRetry(error, attempt)) {
        throw error;
      }

      const delay = this.calculateDelay(attempt);
      logger.warn(`${this.serviceName} retrying after ${delay}ms`, {
        component: this.serviceName,
        action: 'retry',
        metadata: {
          attempt: attempt + 1,
          maxRetries: this.retryConfig.maxRetries,
        },
      });

      await this.sleep(delay);
      return this.executeWithRetry(operation, attempt + 1);
    }
  }

  /**
   * Determine if operation should be retried
   */
  private shouldRetry(error: any, attempt: number): boolean {
    if (attempt >= this.retryConfig.maxRetries) {
      return false;
    }

    // Retry on network errors, timeouts, and 5xx server errors
    const retryableCodes = ['PGRST301', 'PGRST302', 'PGRST116', '23505'];
    const statusCode = error?.statusCode || error?.status;
    
    return (
      retryableCodes.includes(error?.code) ||
      (statusCode >= 500 && statusCode < 600) ||
      error?.message?.includes('network') ||
      error?.message?.includes('timeout')
    );
  }

  /**
   * Calculate delay with exponential backoff
   */
  private calculateDelay(attempt: number): number {
    const delay = Math.min(
      this.retryConfig.initialDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt),
      this.retryConfig.maxDelay
    );
    
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Format error for consistent error handling
   */
  protected formatError(error: any): ServiceError {
    const serviceError: ServiceError = new Error(
      error?.message || 'An unexpected error occurred'
    );
    
    serviceError.name = `${this.serviceName}Error`;
    serviceError.code = error?.code;
    serviceError.details = error?.details;
    serviceError.statusCode = error?.statusCode || error?.status;
    
    return serviceError;
  }

  /**
   * Get current authenticated user
   */
  protected async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      throw this.formatError(error);
    }
    
    if (!user) {
      const error: ServiceError = new Error('User not authenticated');
      error.statusCode = 401;
      throw error;
    }
    
    return user;
  }

  /**
   * Validate required fields
   */
  protected validateRequired(
    data: Record<string, any>,
    requiredFields: string[]
  ): void {
    const missing = requiredFields.filter(field => !data[field]);
    
    if (missing.length > 0) {
      const error: ServiceError = new Error(
        `Missing required fields: ${missing.join(', ')}`
      );
      error.code = 'VALIDATION_ERROR';
      error.statusCode = 400;
      throw error;
    }
  }
}
